import React, { useMemo } from 'react';
import { ListColumnType, ListFilterFieldType, ListFilterType, ListMoreMenuConfig } from 'types/ListTypes';
import { BulkActionsConfig } from 'types/BulkActionTypes';
import AbstractList from 'components/lists/AbstractList';
import JobModal from './JobModal';
import { renderDateRange, renderStatusBadge } from 'components/lists/functions/renderListColumn';
import { useSettings } from 'services/settings/SettingsContext';
import JobForm from './JobForm';

// Enable the bulk actions panel for this list, with Job's finish custom action
export const jobBulkActions: BulkActionsConfig = {
    objectKey: 'job',
    deleteOrDeactivate: 'delete',
    showExport: true,
    customActions: [
        {
            key: 'finish',
            label: 'job.bulk_actions.finish_label',
            confirmTitle: 'job.bulk_actions.finish_confirm_title',
            confirmMessage: 'job.bulk_actions.finish_confirm_message',
        },
    ],
};

// Set the filters for the job detail list, the main job list uses the filter fields below
export const jobFilters: ListFilterType[] = [
    {
        id: 'active',
        label: 'job.general.all_jobs_filter_label',
        params: { 'deleted': false },
        default: true,
    },
    {
        group: 'job.general.status_condition_filter_group_label',
        filters: [
            {
                id: 'job_created',
                label: 'job.status_condition.job_created',
                params: { 'status_condition': 'job_created' },
            },
            {
                id: 'to_schedule',
                label: 'job.status_condition.to_schedule',
                params: { 'status_condition': 'to_schedule' },
            },
            {
                id: 'one_open_purchase',
                label: 'job.status_condition.one_open_purchase',
                params: { 'status_condition': 'one_open_purchase' },
            },
            {
                id: 'all_purchases_delivered',
                label: 'job.status_condition.all_purchases_delivered',
                params: { 'status_condition': 'all_purchases_delivered' },
            },
            {
                id: 'one_reserved_appointment',
                label: 'job.status_condition.one_reserved_appointment',
                params: { 'status_condition': 'one_reserved_appointment' },
            },
            {
                id: 'one_scheduled_appointment',
                label: 'job.status_condition.one_scheduled_appointment',
                params: { 'status_condition': 'one_scheduled_appointment' },
            },
            {
                id: 'running_travel_timer',
                label: 'job.status_condition.running_travel_timer',
                params: { 'status_condition': 'running_travel_timer' },
            },
            {
                id: 'running_work_timer',
                label: 'job.status_condition.running_work_timer',
                params: { 'status_condition': 'running_work_timer' },
            },
            {
                id: 'incomplete',
                label: 'job.status_condition.incomplete',
                params: { 'status_condition': 'incomplete' },
            },
            {
                id: 'completed',
                label: 'job.status_condition.completed',
                params: { 'status_condition': 'completed' },
            },
            {
                id: 'finished',
                label: 'job.status_condition.finished',
                params: { 'status_condition': 'finished' },
            },
        ]
    },
    {
        group: 'general.archive',
        filters: [
            {
                id: 'deleted',
                label: 'job.general.deleted_jobs_label',
                shortName: 'general.deleted',
                params: { 'deleted': true },
            }
        ]
    }
]

// Configure the more menu of this list
export const jobMoreMenu: ListMoreMenuConfig = {
    importObject: 'job',
    inactiveItemsToggle: {
        semantic: 'deleted',
        activeParams: { deleted: true },
        clearFilterNames: ['status'],
    },
};

// Build the filter fields of the job list based on the settings
const buildJobFilterFields = (manualToScheduleActive: boolean): ListFilterFieldType[] => [
    {
        type: 'search',
        name: 'search',
        label: 'forms.search_label',
        placeholder: 'forms.search_placeholder',
        queryParam: 'search',
        width: 1.5
    },
    {
        type: 'dropdown',
        name: 'workflow',
        label: 'job.general.type_label',
        queryParam: 'workflow',
        requiredFeature: 'job_types',
        apiRequest: {
            endpoint: 'get_workflow_list',
            params: { connected_feature: 'job', is_active: true },
            object: 'workflow'
        },
        groupByField: 'division',
        width: 1.25
    },
    {
        type: 'dropdown',
        name: 'status',
        label: 'job.general.status_label',
        queryParam: 'status_condition',
        optionField: 'condition',
        backendField: 'condition',
        translationPrefix: 'job.status_condition.',
        apiRequest: {
            endpoint: 'get_unique_status_for_feature_list/job',
            params: { },
            object: 'status'
        },
        excludeOptionValues: manualToScheduleActive ? [] : ['to_schedule'],
        width: 1.25
    },
    // {
    //     type: 'multiselect',
    //     name: 'labels',
    //     label: 'job.general.labels_label',
    //     queryParam: 'labels__in',
    //     apiRequest: {
    //         endpoint: 'get_label_list',
    //         params: { connected_feature: 'jobs', deleted: false },
    //         object: 'label',
    //     },
    //     width: 1.5
    // },
    {
        type: 'daterange',
        name: 'appointment_range',
        label: 'job.general.appointment_range',
        queryParam: 'appointments__start_datetime',
        width: 2
    },
    // {
    //     type: 'multiselect',
    //     name: 'assignees',
    //     label: 'job.schedule_job.assignees_label',
    //     queryParam: 'appointments__assignees__in',
    //     optionField: 'full_name',
    //     apiRequest: {
    //         endpoint: 'get_user_list',
    //         params: { is_active: true },
    //         object: 'user',
    //     },
    //     width: 1.5
    // },
    // {
    //     type: 'dropdown',
    //     name: 'managed_by_user',
    //     label: 'job.general.managed_by_user_label',
    //     queryParam: 'managed_by_user',
    //     requiredFeature: 'extended_lists', 
    //     optionField: 'full_name',
    //     apiRequest: {
    //         endpoint: 'get_user_list',
    //         params: { office_access: true, is_active: true },
    //         object: 'office_user',
    //     },
    //     width: 1.5
    // },
    {
        type: 'dropdown',
        name: 'division',
        label: 'job.general.division_label',
        queryParam: 'division',
        requiredFeature: 'divisions',
        defaultValueFromContext: 'user_division',
        apiRequest: {
            endpoint: 'get_division_list',
            params: { is_active: true },
            object: 'division'
        },
        width: 1.5
    }
];

const JobList: React.FC = () => {
    const { environmentSettings, userLocale } = useSettings();

    // Build the filter fields, hides the to schedule status option when the manual to schedule setting is off
    const jobFilterFields = useMemo(
        () => buildJobFilterFields(Boolean(environmentSettings.scheduling_manual_to_schedule_status)),
        [environmentSettings.scheduling_manual_to_schedule_status]
    );

    // Specify the options of this list:
    const query = {
        endpoint: 'get_job_list',
        defaultParams: { 'deleted': false },
        object: 'job'
    }
    const objectName = 'job.general.object_name'

    // Set isPaginated to true if this list may have 100.000+ items:
    const isPaginated = true

    // Specify form, detail modal or detailpage for this list
    const formModal = <JobForm />;
    const modalSize = 'add-form';
    const detailModal = <JobModal />;
    const detailModalName = 'job-modal';
    const onRowClick = 'detailModal'

    // Specify the columns of this list
    const columns: ListColumnType[] = useMemo(() => [
        { 
            width: '1.5fr', 
            field: 'number', 
            label: 'job.general.job_number_label' 
        },
        {
            width: '6fr', 
            field: 'client_name', 
            orderField: 'client__name',
            label: 'job.general.client_label' 
        },
        { 
            width: '5fr', 
            field: 'main_description_plain',
            orderField: 'main_description',
            label: 'job.general.main_description_label' 
        },
        { 
            width: '2fr', 
            field: 'type_name', 
            orderField: 'workflow__name', 
            requiredFeature: 'job_types', 
            label: 'job.general.type_label' 
        },
        {
            width: '2.5fr',
            field: 'appointment_range',
            label: 'job.general.appointment_date',
            orderField: 'first_appointment_start',
            render: (_, row) => renderDateRange(row.first_appointment_start, row.last_appointment_end, userLocale)
        },
        { 
            width: '144px', 
            field: 'status', 
            label: 'job.general.status_label', 
            disableOrdering: true,
            render: (_, row) => renderStatusBadge(row.status_name, row.status_condition) 
        }
    ], []);

    // Get the create jobs setting, to either hide or show the add button
    const { job_create_jobs } = environmentSettings;

    return (
        <AbstractList
            query={query}
            objectName={objectName}
            extendedWidth={true}
            columns={columns}
            filterFields={jobFilterFields}
            formModal={formModal}
            modalSize={modalSize}
            detailModal={detailModal}
            detailModalName={detailModalName}
            isPaginated={isPaginated}
            onRowClick={onRowClick}
            showAddButton={job_create_jobs}
            bulkActions={jobBulkActions}
            moreMenu={jobMoreMenu}
        />
    );
};

export default JobList;